import React, { useState, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, Table, TableHead, 
    TableRow, TableCell, TableBody, Paper, 
    CircularProgress, Alert, Stack, IconButton,
    Tooltip, useTheme, useMediaQuery, Chip, alpha,
    FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
    CloudUpload, Delete, Close,
    FileDownload, CheckCircle, Error
} from '@mui/icons-material';
import Papa from 'papaparse';
import { toast } from 'react-toastify';
import api from '../utils/Api';

const BulkStudentModal = ({ open, onClose, onSuccess, classes, academicYears }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [file, setFile] = useState(null);
    const [previewData, setPreviewData] = useState([]);
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState(null);
    const [defaultAcademicYear, setDefaultAcademicYear] = useState('');
    const [importErrors, setImportErrors] = useState([]);

    React.useEffect(() => {
        if (academicYears && academicYears.length > 0 && !defaultAcademicYear) {
            setDefaultAcademicYear(academicYears[0]);
        }
    }, [academicYears, defaultAcademicYear]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        if (selectedFile.type !== "text/csv" && !selectedFile.name.endsWith('.csv')) {
            setError("Please upload a CSV file");
            return;
        }

        setFile(selectedFile);
        setError(null);

        Papa.parse(selectedFile, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                if (results.data && results.data.length > 0) {
                    // Validating headers roughly
                    const requiredHeaders = ['name', 'email', 'password', 'className', 'gender', 'academicYear'];
                    const headers = Object.keys(results.data[0]);
                    const missing = requiredHeaders.filter(h => !headers.includes(h));
                    
                    if (missing.length > 0) {
                        setError(`Missing required columns: ${missing.join(', ')}`);
                        setPreviewData([]);
                    } else {
                        // Check if they used className or classId
                        setPreviewData(results.data.slice(0, 50)); // Preview first 50
                    }
                } else {
                    setError("No data found in file");
                }
            }
        });
    };

    const handleRemoveFile = () => {
        setFile(null);
        setPreviewData([]);
        setError(null);
        setImportErrors([]);
    };

    const downloadTemplate = () => {
        const headers = [
            "name", "email", "password", "admissionNumber", "gender", "dob", "className", "academicYear", 
            "religion", "hometown", "languageSpoken", "guardianPhone", "guardianPhone2", 
            "guardianOccupation", "fatherName", "fatherOccupation", 
            "motherName", "motherOccupation"
        ];
        const csvContent = headers.join(",") + "\nExample Student,student@example.com,p@ssword123,ADM-001,Male,2015-05-15,BASIC 1,2025-2026,Christianity,Accra,English,0240000000,0500000000,Trader,John Doe,Fisherman,Jane Doe,Teacher";
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = "student_import_template.csv";
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const handleImport = async () => {
        if (!file) return;

        setImporting(true);
        setError(null);

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: async (results) => {
                try {
                    setImportErrors([]);
                    // Filter out completely empty objects (e.g., from trailing comma-only rows)
                    const validData = results.data.filter(row => row.name && row.name.trim() !== '');

                    const payload = {
                        students: validData.map(row => ({
                            ...row,
                            academicYear: row.academicYear || defaultAcademicYear
                            // Ensure numeric fields are cleaned or validated if needed
                        }))
                    };

                    const res = await api.post('/students/bulk', payload);
                    const { summary, results: backendResults } = res.data;

                    if (summary.errorCount > 0) {
                        toast.warning(`Imported ${summary.successCount} students. ${summary.errorCount} failed.`);
                        if (backendResults && backendResults.errors && backendResults.errors.length > 0) {
                            setImportErrors(backendResults.errors);
                        }
                        if (summary.successCount > 0) onSuccess();
                    } else {
                        toast.success(`Successfully imported all ${summary.successCount} students.`);
                        onSuccess();
                        handleClose();
                    }
                } catch (err) {
                    console.error("Bulk import failed:", err);
                    setError(err.response?.data?.message || "Internal server error during import");
                } finally {
                    setImporting(false);
                }
            }
        });
    };

    const handleClose = () => {
        setFile(null);
        setPreviewData([]);
        setError(null);
        setImportErrors([]);
        onClose();
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="md" 
            fullWidth
            fullScreen={isMobile}
        >
            <DialogTitle sx={{ borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CloudUpload color="primary" />
                    <Typography variant="h6">Bulk Student Import</Typography>
                </Box>
                <IconButton onClick={handleClose} size="small"><Close /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    <Alert severity="info">
                        Use this feature to register many students at once. 
                        <strong> Important:</strong> Email addresses must be unique. Name, Email, Password, Class, Gender, and Academic Year are <strong>mandatory</strong>.
                    </Alert>

                    <Box sx={{ p: 3, border: `2px dashed ${theme.palette.divider}`, borderRadius: 2, textAlign: 'center', bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                        {!file ? (
                            <>
                                <input
                                    type="file"
                                    accept=".csv"
                                    id="bulk-student-file"
                                    style={{ display: 'none' }}
                                    onChange={handleFileChange}
                                />
                                <label htmlFor="bulk-student-file">
                                    <Button variant="contained" component="span" startIcon={<CloudUpload />}>
                                        Select CSV File
                                    </Button>
                                </label>
                                <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                                    Upload a CSV file containing student records
                                </Typography>
                            </>
                        ) : (
                            <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
                                <Chip 
                                    icon={<CheckCircle />} 
                                    label={file.name} 
                                    color="success" 
                                    onDelete={handleRemoveFile} 
                                    variant="outlined"
                                />
                                <Typography variant="body2">{file.size} bytes</Typography>
                            </Stack>
                        )}
                    </Box>

                    {error && <Alert severity="error">{error}</Alert>}

                    {!file && (
                        <Button 
                            variant="text" 
                            startIcon={<FileDownload />} 
                            onClick={downloadTemplate}
                        >
                            Download CSV Template
                        </Button>
                    )}

                    {file && (
                        <FormControl fullWidth size="small">
                            <InputLabel>Default Academic Year</InputLabel>
                            <Select
                                value={defaultAcademicYear}
                                label="Default Academic Year"
                                onChange={(e) => setDefaultAcademicYear(e.target.value)}
                            >
                                {academicYears && academicYears.map((year) => (
                                    <MenuItem key={year} value={year}>{year}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {importErrors.length > 0 && (
                        <Box mt={1}>
                            <Alert severity="warning">Some rows failed to import. See details below:</Alert>
                            <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', mt: 1, p: 2 }}>
                                {importErrors.map((err, idx) => (
                                    <Typography key={idx} variant="body2" color="error" gutterBottom>
                                        <strong>{err.name ? err.name : (err.email ? err.email : `Row ${idx + 1}`)}:</strong> {err.message}
                                    </Typography>
                                ))}
                            </Paper>
                        </Box>
                    )}

                    {previewData.length > 0 && (
                        <Box>
                            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                                Preview (First {previewData.length} records)
                            </Typography>
                            <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>NAME</TableCell>
                                            <TableCell>EMAIL</TableCell>
                                            <TableCell>ADMISSION NO</TableCell>
                                            <TableCell>GENDER</TableCell>
                                            <TableCell>CLASS</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {previewData.map((row, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>{row.name}</TableCell>
                                                <TableCell>{row.email}</TableCell>
                                                <TableCell>{row.admissionNumber || "-"}</TableCell>
                                                <TableCell>{row.gender}</TableCell>
                                                <TableCell>{row.className || row.classId || row.class || "Unassigned"}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Paper>
                        </Box>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
                <Button onClick={handleClose} disabled={importing}>Cancel</Button>
                <Button 
                    onClick={handleImport} 
                    variant="contained" 
                    color="primary"
                    disabled={!file || importing || !!error}
                    startIcon={importing ? <CircularProgress size={20} /> : <CheckCircle />}
                >
                    {importing ? "Importing..." : "Start Import"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkStudentModal;
